import React, { useEffect, useState, useMemo } from 'react';
import { queryAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';

// Group flat DESC history into sessions (queries within 30 min = same conversation)
function groupSessions(history) {
  if (!history.length) return [];
  const asc = [...history].reverse(); // process oldest → newest
  const sessions = [];
  let current = [asc[0]];
  for (let i = 1; i < asc.length; i++) {
    const diff = Math.abs(new Date(asc[i].created_at) - new Date(asc[i - 1].created_at));
    if (diff < 30 * 60 * 1000) {
      current.push(asc[i]);
    } else {
      sessions.push(current);
      current = [asc[i]];
    }
  }
  sessions.push(current);
  return sessions.reverse(); // newest session first
}

function fmtTime(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true
  });
}

const INTENT_STYLE = {
  lookup:      { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0', dot: '#16a34a', label: '🔍 Lookup' },
  aggregation: { bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe', dot: '#2563eb', label: '📊 Aggregation' },
  trend:       { bg: '#E8F6EE', color: '#1B5E3B', border: '#81C784', dot: '#1B5E3B', label: '📈 Trend' },
  'top n':     { bg: '#fff7ed', color: '#ea580c', border: '#fed7aa', dot: '#ea580c', label: '🏆 Top N' },
};
function ic(intent) {
  return INTENT_STYLE[(intent || '').toLowerCase()] || { bg: '#f8fafc', color: '#475569', border: '#e2e8f0', dot: '#9ca3af', label: intent || 'lookup' };
}

function confColor(s) { return s >= 0.8 ? '#059669' : s >= 0.5 ? '#d97706' : '#dc2626'; }
function confBg(s)    { return s >= 0.8 ? '#ecfdf5' : s >= 0.5 ? '#fffbeb' : '#fef2f2'; }

function tabActiveStyle(tab) {
  if (tab === 'all') return { background: 'linear-gradient(135deg, #1B5E3B, #2E7D52)', borderColor: 'transparent', color: '#fff' };
  const s = INTENT_STYLE[tab];
  if (!s) return {};
  return { background: s.bg, borderColor: s.border, color: s.color, fontWeight: 700, boxShadow: `0 0 0 3px ${s.border}` };
}

const TABS = ['all', 'lookup', 'aggregation', 'trend', 'top n'];

export default function History() {
  const [history,   setHistory]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [pageIn,    setPageIn]    = useState(false);
  const [search,    setSearch]    = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [expanded,  setExpanded]  = useState(new Set());  // session indices expanded
  const [showSQL,   setShowSQL]   = useState(new Set());  // session indices showing SQL
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => setPageIn(true), 60);
    queryAPI.history(200)
      .then(r => setHistory(r.data.history || []))
      .catch(() => {})
      .finally(() => setLoading(false));
    return () => clearTimeout(t);
  }, []);

  const stats = useMemo(() => {
    if (!history.length) return { total: 0, avgConf: 0, fastest: 0, aggregations: 0, lookups: 0 };
    const total = history.length;
    const avgConf = Math.round(history.reduce((s, h) => s + (h.confidence_score || 0), 0) / total * 100);
    const msArr = history.map(h => h.execution_ms).filter(Boolean);
    const fastest = msArr.length ? Math.min(...msArr) : 0;
    const aggregations = history.filter(h => (h.intent || '').toLowerCase() === 'aggregation').length;
    const lookups = history.filter(h => (h.intent || '').toLowerCase() === 'lookup').length;
    return { total, avgConf, fastest, aggregations, lookups };
  }, [history]);

  const tabCounts = useMemo(() => ({
    all:         history.length,
    lookup:      history.filter(h => (h.intent || '').toLowerCase() === 'lookup').length,
    aggregation: history.filter(h => (h.intent || '').toLowerCase() === 'aggregation').length,
    trend:       history.filter(h => (h.intent || '').toLowerCase() === 'trend').length,
    'top n':     history.filter(h => (h.intent || '').toLowerCase() === 'top n').length,
  }), [history]);

  const filtered = useMemo(() => history.filter(h => {
    const matchTab = activeTab === 'all' || (h.intent || '').toLowerCase() === activeTab;
    const q = search.toLowerCase();
    const matchSearch = !q || h.query_text?.toLowerCase().includes(q) || h.sql_generated?.toLowerCase().includes(q);
    return matchTab && matchSearch;
  }), [history, activeTab, search]);

  const sessions = useMemo(() => groupSessions(filtered), [filtered]);

  const toggleExpand = (si) => setExpanded(prev => { const n = new Set(prev); n.has(si) ? n.delete(si) : n.add(si); return n; });
  const toggleSQL    = (si) => setShowSQL(prev  => { const n = new Set(prev); n.has(si) ? n.delete(si) : n.add(si); return n; });

  const runQuery = (q) => navigate('/', { state: { runQuery: q } });
  const copyText = (t) => navigator.clipboard?.writeText(t).catch(() => {});

  return (
    <div style={{
      ...S.page,
      opacity: pageIn ? 1 : 0,
      transform: pageIn ? 'none' : 'translateY(14px)',
      transition: 'opacity 0.4s ease, transform 0.4s ease'
    }}>
      {/* Header */}
      <div style={{ animation: 'fadeInUp 0.4s ease 0.05s both' }}>
        <h1 style={S.title}>Query History</h1>
        <p style={S.subtitle}>Your recent conversations and queries</p>
      </div>

      {/* Stats strip */}
      <div style={S.statsRow}>
        {[
          { label: 'Total queries',  value: stats.total },
          { label: 'Avg confidence', value: stats.avgConf + '%' },
          { label: 'Fastest query',  value: stats.fastest + 'ms' },
          { label: 'Aggregations',   value: stats.aggregations },
          { label: 'Lookups',        value: stats.lookups },
        ].map(({ label, value }) => (
          <div key={label} style={S.statCard}>
            <div style={S.statValue}>{value}</div>
            <div style={S.statLabel}>{label}</div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div style={S.searchWrap}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9b9b9b" strokeWidth="2" style={S.searchIcon}>
          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search queries or SQL..."
          style={S.searchInput}
        />
        {search && (
          <button onClick={() => setSearch('')} style={S.searchClear}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        )}
      </div>

      {/* Filter tabs */}
      <div style={S.tabs}>
        {TABS.map(tab => {
          const isActive = activeTab === tab;
          const st = INTENT_STYLE[tab];
          return (
            <button key={tab} onClick={() => setActiveTab(tab)}
              style={{ ...S.tab, ...(isActive ? tabActiveStyle(tab) : {}) }}>
              {tab !== 'all' && (
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: isActive ? (st?.dot || '#1B5E3B') : '#c0bdb8', flexShrink: 0 }} />
              )}
              {tab === 'all' ? 'All' : tab === 'top n' ? 'Top N' : tab.charAt(0).toUpperCase() + tab.slice(1)}
              <span style={{ ...S.tabCount, ...(isActive ? S.tabCountActive : {}), background: isActive && tab !== 'all' ? `${st?.border}` : undefined, color: isActive && tab !== 'all' ? st?.color : undefined }}>
                {tabCounts[tab] ?? 0}
              </span>
            </button>
          );
        })}
      </div>

      {/* Content */}
      {loading ? (
        <div style={S.loadingBox}>
          {[0,1,2].map(i => (
            <div key={i} style={{ width: 8, height: 8, borderRadius: '50%', background: '#1B5E3B',
              animation: `bounce 1.4s ease-in-out ${i * 0.2}s infinite` }} />
          ))}
        </div>
      ) : sessions.length === 0 ? (
        <div style={{ ...S.empty, animation: 'fadeIn 0.4s ease 0.2s both' }}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5">
            <polyline points="12 8 12 12 14 14"/>
            <path d="M3.05 11a9 9 0 1 0 .5-4.5"/><polyline points="3 3 3 9 9 9"/>
          </svg>
          {search ? 'No matching queries found.' : 'No queries yet — go to Home and ask something!'}
        </div>
      ) : (
        <div style={S.list}>
          {sessions.map((session, si) => {
            const isMulti = session.length > 1;
            const main    = session[0];
            const isExp   = expanded.has(si);
            const isSql   = showSQL.has(si);
            const style   = ic(main.intent);

            return (
              <div key={si} className="history-card" style={{ ...S.card, animation: `fadeInUp 0.3s ease ${si * 35}ms both` }}>

                {/* ── Card header row ── */}
                <div style={S.cardHead}>
                  <div style={S.cardHeadLeft}>
                    {isMulti && (
                      <span style={S.turnsBadge}>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                        </svg>
                        {session.length} turns
                      </span>
                    )}
                    <span style={{ ...S.intentBadge, background: style.bg, color: style.color, borderColor: style.border }}>
                      {main.intent || 'lookup'}
                    </span>
                    <span style={S.headQuery}>{main.query_text}</span>
                  </div>
                  <div style={S.cardHeadRight}>
                    <span style={S.headTime}>{fmtTime(main.created_at)}</span>
                    <button onClick={() => copyText(main.query_text)} style={S.iconBtn} title="Copy query">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="9" y="9" width="13" height="13" rx="2"/>
                        <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
                      </svg>
                    </button>
                    <button onClick={() => runQuery(main.query_text)} style={S.runBtn} className="btn-lift">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polygon points="5 3 19 12 5 21 5 3"/>
                      </svg>
                      Run
                    </button>
                    {isMulti && (
                      <button onClick={() => toggleExpand(si)} style={S.chevronBtn}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                          style={{ transform: isExp ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease' }}>
                          <polyline points="6 9 12 15 18 9"/>
                        </svg>
                      </button>
                    )}
                  </div>
                </div>

                {/* ── Multi-turn expanded turns ── */}
                {isMulti && isExp && (
                  <div style={S.turns}>
                    {session.slice(1).map((turn, ti) => {
                      const ts = ic(turn.intent);
                      return (
                        <div key={ti} style={S.turnRow}>
                          <span style={S.turnLabel}>Turn {ti + 2}</span>
                          <span style={{ ...S.intentBadge, background: ts.bg, color: ts.color, borderColor: ts.border, fontSize: 10, padding: '2px 7px' }}>
                            {turn.intent || 'lookup'}
                          </span>
                          <span style={S.turnText}>{turn.query_text}</span>
                          <button onClick={() => runQuery(turn.query_text)} style={S.runBtnSm} className="btn-lift">
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <polygon points="5 3 19 12 5 21 5 3"/>
                            </svg>
                            Run
                          </button>
                        </div>
                      );
                    })}
                    <button onClick={() => toggleSQL(si)} style={S.showSqlBtn}>
                      {isSql ? 'Hide SQL' : `Show all ${session.length} turns with SQL`}
                    </button>
                    {isSql && main.sql_generated && (
                      <pre style={S.sql}>{main.sql_generated}</pre>
                    )}
                  </div>
                )}

                {/* ── Single-turn body (always visible) ── */}
                {!isMulti && (
                  <div style={S.body}>
                    <div style={S.bodyQuery}>{main.query_text}</div>
                    <div style={S.metaRow}>
                      <span style={{ ...S.chip, background: style.bg, color: style.color, borderColor: style.border }}>
                        {main.intent || 'lookup'}
                      </span>
                      <span style={S.chip}>{main.row_count ?? '—'} rows</span>
                      <span style={S.chip}>{main.execution_ms ?? '—'}ms</span>
                      <span style={{
                        ...S.chip,
                        background: confBg(main.confidence_score),
                        color: confColor(main.confidence_score),
                        borderColor: confColor(main.confidence_score) + '40'
                      }}>
                        {Math.round((main.confidence_score || 0) * 100)}% confidence
                      </span>
                      <span style={S.metaSpacer} />
                      <span style={{ ...S.chip, background: 'none', border: 'none', color: '#9b9b9b', fontSize: 11 }}>
                        {fmtTime(main.created_at)}
                      </span>
                      <button onClick={() => copyText(main.sql_generated)} style={S.iconBtn} title="Copy SQL">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="9" y="9" width="13" height="13" rx="2"/>
                          <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
                        </svg>
                      </button>
                      <button onClick={() => runQuery(main.query_text)} style={S.runBtnSm} className="btn-lift">
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <polygon points="5 3 19 12 5 21 5 3"/>
                        </svg>
                        Run
                      </button>
                    </div>
                    {main.sql_generated && (
                      <pre style={S.sql}>{main.sql_generated}</pre>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <style>{`
        .history-card:hover {
          box-shadow: 0 8px 28px rgba(27,94,59,0.1) !important;
          border-color: #81C784 !important;
          transform: translateY(-2px);
        }
        .history-card:hover .run-btn-glow {
          box-shadow: 0 4px 14px rgba(27,94,59,0.45) !important;
        }
      `}</style>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────

const S = {
  page: { padding: '40px 48px', maxWidth: 960 },
  title: { fontSize: 26, fontWeight: 700, color: '#1a1a1a', letterSpacing: '-0.4px', margin: 0 },
  subtitle: { fontSize: 14, color: '#6b6b6b', marginTop: 5, marginBottom: 24 },

  statsRow: {
    display: 'flex', gap: 12, marginBottom: 20,
  },
  statCard: {
    flex: 1, background: '#fff', border: '1px solid #cce8d8', borderRadius: 12,
    padding: '16px 18px', textAlign: 'center',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
  },
  statValue: { fontSize: 22, fontWeight: 800, color: '#1B5E3B', lineHeight: 1 },
  statLabel: { fontSize: 11, color: '#9b9b9b', marginTop: 5, fontWeight: 500 },

  searchWrap: {
    position: 'relative', marginBottom: 14,
  },
  searchIcon: { position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' },
  searchInput: {
    width: '100%', boxSizing: 'border-box',
    padding: '10px 40px 10px 38px',
    border: '1.5px solid #cce8d8', borderRadius: 10,
    fontSize: 13.5, color: '#1a1a1a', background: '#fff',
    outline: 'none', transition: 'border-color 0.15s'
  },
  searchClear: {
    position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
    background: 'none', border: 'none', cursor: 'pointer', color: '#9b9b9b',
    display: 'flex', alignItems: 'center', padding: 2
  },

  tabs: { display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' },
  tab: {
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '6px 14px', borderRadius: 20, border: '1.5px solid #cce8d8',
    background: '#fff', color: '#6b6b6b', fontSize: 12.5, fontWeight: 500,
    cursor: 'pointer', transition: 'all 0.15s ease'
  },
  tabActive: {
    background: 'linear-gradient(135deg, #1B5E3B, #2E7D52)',
    borderColor: 'transparent', color: '#fff'
  },
  tabCount: {
    background: '#e4f5ed', color: '#6b6b6b',
    fontSize: 10.5, fontWeight: 700,
    padding: '1px 7px', borderRadius: 10,
    minWidth: 18, textAlign: 'center'
  },
  tabCountActive: { background: 'rgba(255,255,255,0.25)', color: '#fff' },

  loadingBox: { display: 'flex', gap: 8, padding: '48px 0', justifyContent: 'center' },
  empty: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: 12, padding: '64px 0', color: '#9b9b9b', fontSize: 14
  },
  list: { display: 'flex', flexDirection: 'column', gap: 10 },

  card: {
    background: '#fff', border: '1px solid #cce8d8', borderRadius: 14,
    overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
    transition: 'box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease'
  },

  cardHead: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '14px 18px', gap: 12
  },
  cardHeadLeft: { display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 },
  cardHeadRight: { display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 },

  turnsBadge: {
    display: 'flex', alignItems: 'center', gap: 4,
    background: '#E8F6EE', color: '#1B5E3B', borderRadius: 10,
    padding: '3px 8px', fontSize: 11, fontWeight: 600,
    border: '1px solid #81C784', flexShrink: 0
  },
  intentBadge: {
    fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 10,
    border: '1px solid', flexShrink: 0, textTransform: 'capitalize'
  },
  headQuery: {
    fontSize: 13.5, fontWeight: 600, color: '#1a1a1a',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1
  },
  headTime: { fontSize: 11, color: '#9b9b9b', whiteSpace: 'nowrap' },

  iconBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    color: '#c0bdb8', padding: '4px 5px', borderRadius: 6,
    display: 'flex', alignItems: 'center',
    transition: 'color 0.15s'
  },
  runBtn: {
    display: 'flex', alignItems: 'center', gap: 5,
    background: 'linear-gradient(135deg, #1B5E3B, #2E7D52)', color: '#fff',
    border: 'none', borderRadius: 8, padding: '6px 13px',
    fontSize: 12, fontWeight: 600, cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(27,94,59,0.3)'
  },
  runBtnSm: {
    display: 'flex', alignItems: 'center', gap: 4,
    background: 'linear-gradient(135deg, #1B5E3B, #2E7D52)', color: '#fff',
    border: 'none', borderRadius: 7, padding: '4px 10px',
    fontSize: 11, fontWeight: 600, cursor: 'pointer',
    boxShadow: '0 2px 6px rgba(27,94,59,0.25)'
  },
  chevronBtn: {
    background: 'none', border: '1px solid #cce8d8', borderRadius: 7,
    color: '#6b6b6b', cursor: 'pointer', padding: '4px 6px',
    display: 'flex', alignItems: 'center'
  },

  turns: {
    borderTop: '1px solid #e4f5ed', background: '#f2faf8',
    padding: '12px 18px', display: 'flex', flexDirection: 'column', gap: 8
  },
  turnRow: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '7px 10px', borderRadius: 8, background: '#fff',
    border: '1px solid #e4f5ed'
  },
  turnLabel: {
    fontSize: 11, fontWeight: 700, color: '#1B5E3B',
    background: '#E8F6EE', border: '1px solid #C8EDD6',
    borderRadius: 6, padding: '2px 7px', flexShrink: 0
  },
  turnText: {
    fontSize: 13, color: '#1a1a1a', flex: 1,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
  },
  showSqlBtn: {
    background: 'none', border: '1px dashed #81C784',
    color: '#1B5E3B', borderRadius: 8, padding: '6px 14px',
    fontSize: 12, fontWeight: 500, cursor: 'pointer',
    alignSelf: 'flex-start', marginTop: 2
  },

  body: { borderTop: '1px solid #e4f5ed', padding: '14px 18px' },
  bodyQuery: { fontSize: 14, fontWeight: 600, color: '#1a1a1a', marginBottom: 10 },
  metaRow: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 12 },
  metaSpacer: { flex: 1 },
  chip: {
    background: '#E8F6EE', color: '#0A3D1F', fontSize: 11,
    padding: '3px 9px', borderRadius: 10, border: '1px solid #C8EDD6', fontWeight: 500
  },
  sql: {
    background: '#071A0E', color: '#e2e8f0',
    padding: '14px 18px', borderRadius: 10, fontSize: 11.5,
    fontFamily: 'JetBrains Mono, ui-monospace, monospace',
    overflowX: 'auto', margin: 0, maxHeight: 160,
    overflowY: 'auto', whiteSpace: 'pre-wrap',
    wordBreak: 'break-word', lineHeight: 1.7
  },
};
